import React, { useEffect, useState } from "react";
import { db } from "../../api";
import { collection, query, where, getDocs } from "../../api";
import { useAuth } from "../../context/AuthContext";

export default function RentalHistory() {
  const { currentUser } = useAuth();
  const [stats, setStats] = useState({
    totalRentals: 0,
    totalSpent: 0,
    activeRentals: 0,
    completedRentals: 0,
    mostRentedCategory: 'N/A',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        const q = query(
          collection(db, "reservations"),
          where("userId", "==", currentUser.uid)
        );

        const snapshot = await getDocs(q);
        const reservations = snapshot.docs.map(doc => doc.data());

        // Calculate statistics
        const totalRentals = reservations.length;
        const totalSpent = reservations.reduce((sum, r) => sum + (parseFloat(r.totalCost) || 0), 0);
        const activeRentals = reservations.filter(r => r.status === 'active' || r.status === 'pending').length;
        const completedRentals = reservations.filter(r => r.status === 'completed').length;

        // Find most rented category
        const categoryCounts = {};
        reservations.forEach(r => {
          const category = r.category || 'Unknown';
          categoryCounts[category] = (categoryCounts[category] || 0) + 1;
        });

        const mostRentedCategory = Object.keys(categoryCounts).length > 0
          ? Object.entries(categoryCounts).sort((a, b) => b[1] - a[1])[0][0]
          : 'N/A';

        setStats({
          totalRentals,
          totalSpent: totalSpent.toFixed(2),
          activeRentals,
          completedRentals,
          mostRentedCategory,
        });
      } catch (error) {
        console.error('Error fetching rental stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [currentUser]);

  if (loading) {
    return (
      <div className="bg-white rounded shadow p-6">
        <h3 className="text-burgundy text-xl font-medium mb-4">Rental History</h3>
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded shadow p-6">
      <h3 className="text-burgundy text-xl font-medium mb-6">Rental Statistics</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-blue-50 border border-blue-200 rounded p-4">
          <p className="text-blue-600 text-sm font-medium mb-1">Total Rentals</p>
          <p className="text-3xl font-bold text-blue-800">{stats.totalRentals}</p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded p-4">
          <p className="text-green-600 text-sm font-medium mb-1">Total Spent</p>
          <p className="text-3xl font-bold text-green-800">${stats.totalSpent}</p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
          <p className="text-yellow-600 text-sm font-medium mb-1">Active Rentals</p>
          <p className="text-3xl font-bold text-yellow-800">{stats.activeRentals}</p>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded p-4">
          <p className="text-purple-600 text-sm font-medium mb-1">Completed Rentals</p>
          <p className="text-3xl font-bold text-purple-800">{stats.completedRentals}</p>
        </div>

        <div className="bg-indigo-50 border border-indigo-200 rounded p-4 md:col-span-2">
          <p className="text-indigo-600 text-sm font-medium mb-1">Most Rented Category</p>
          <p className="text-2xl font-bold text-indigo-800">{stats.mostRentedCategory}</p>
        </div>
      </div>
    </div>
  );
}
